if (!customElements.get('creators-form')) {
  customElements.define(
    'creators-form',
    class CreatorsForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.steps = Array.from(this.querySelectorAll('[data-creators-step]'));
        if (!this.form || this.steps.length === 0) return;

        this.progress = this.querySelector('[data-creators-progress]');
        this.progressSegments = this.progress ? Array.from(this.progress.children) : [];
        this.backButton = this.querySelector('[data-creators-back]');
        this.nextButton = this.querySelector('[data-creators-next]');
        this.submitButton = this.querySelector('[data-creators-submit]');
        this.currentStep = 0;

        this.nextButton && this.nextButton.addEventListener('click', this.onNext.bind(this));
        this.backButton && this.backButton.addEventListener('click', this.onBack.bind(this));

        this.initCombinedGroups();
        this.initConditionalRate();
        this.initFieldGroups();
        this.initSubmitGate();
        this.initSubmitGuard();
        this.initAjaxSubmit();
        this.initDialog();

        if (this.progress) this.progress.hidden = false;
        if (this.nextButton) this.nextButton.hidden = false;
        this.showStep(0, false);
      }

      onNext(event) {
        event.preventDefault();
        const invalid = this.getInvalidField(this.currentStep);
        if (invalid) {
          this.reportInvalid(invalid);
          return;
        }
        this.showStep(this.currentStep + 1);
      }

      onBack(event) {
        event.preventDefault();
        this.showStep(this.currentStep - 1);
      }

      getInvalidField(stepIndex) {
        const step = this.steps[stepIndex];

        const fields = step.querySelectorAll('input, select, textarea');
        for (const field of fields) {
          if (field.type === 'hidden' || field.type === 'checkbox' || field.type === 'radio' || field.disabled) {
            continue;
          }
          if (!field.checkValidity()) return field;
        }

        const choiceGroups = step.querySelectorAll('[data-require-one]');
        for (const group of choiceGroups) {
          const checkboxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
          if (checkboxes.length === 0) continue;
          if (!checkboxes.some((checkbox) => checkbox.checked)) return checkboxes[0];
          checkboxes[0].setCustomValidity('');
        }

        const fieldGroups = step.querySelectorAll('[data-require-one-field]');
        for (const group of fieldGroups) {
          const inputs = Array.from(group.querySelectorAll('input:not([type="checkbox"]):not([type="hidden"])'));
          if (inputs.length === 0) continue;
          if (!inputs.some((input) => input.value.trim() !== '')) return inputs[0];
          inputs.forEach((input) => input.setCustomValidity(''));
        }

        return null;
      }

      reportInvalid(field) {
        const group = field.closest('[data-require-one], [data-require-one-field]');
        if (group) {
          field.setCustomValidity(group.dataset.requireOne || group.dataset.requireOneField || 'Please complete this step.');
        }
        field.reportValidity();
      }

      validateAllSteps() {
        for (let i = 0; i < this.steps.length; i++) {
          const invalid = this.getInvalidField(i);
          if (invalid) {
            this.showStep(i, false);
            this.reportInvalid(invalid);
            return false;
          }
        }
        return true;
      }

      initCombinedGroups() {
        this.querySelectorAll('[data-combined-group]').forEach((group) => {
          const output = group.querySelector('input[type="hidden"][data-combined-output]');
          const checkboxes = Array.from(group.querySelectorAll('input[type="checkbox"]'));
          if (!output || checkboxes.length === 0) return;

          const update = () => {
            output.value = checkboxes
              .filter((checkbox) => checkbox.checked)
              .map((checkbox) => checkbox.dataset.combinedLabel || checkbox.value)
              .join(', ');
            checkboxes[0].setCustomValidity('');
          };

          checkboxes.forEach((checkbox) => {
            checkbox.addEventListener('change', update);
          });
          update();
        });
      }

      initConditionalRate() {
        const toggle = this.querySelector('[data-creators-paid-toggle]');
        const rateWrapper = this.querySelector('[data-creators-rate]');
        if (!toggle || !rateWrapper) return;

        const rateInput = rateWrapper.querySelector('input');
        const sync = () => {
          rateWrapper.hidden = !toggle.checked;
          if (rateInput) {
            rateInput.disabled = !toggle.checked;
            rateInput.required = toggle.checked;
          }
        };
        toggle.addEventListener('change', sync);
        sync();
      }

      initFieldGroups() {
        this.querySelectorAll('[data-require-one-field]').forEach((group) => {
          group.addEventListener('input', () => {
            group.querySelectorAll('input').forEach((input) => input.setCustomValidity(''));
          });
        });
      }

      initSubmitGate() {
        const lastStep = this.steps[this.steps.length - 1];
        if (!lastStep || !this.submitButton) return;

        const update = () => this.updateSubmitState();
        lastStep.addEventListener('change', update);
        lastStep.addEventListener('input', update);
      }

      updateSubmitState() {
        if (!this.submitButton || this.currentStep !== this.steps.length - 1) return;

        let valid = true;
        for (let i = 0; i < this.steps.length; i++) {
          if (this.getInvalidField(i)) {
            valid = false;
            break;
          }
        }
        this.submitButton.disabled = !valid;
      }

      initSubmitGuard() {
        this.form.addEventListener('submit', (event) => {
          if (this.submitted) {
            event.preventDefault();
            return;
          }
          if (!this.validateAllSteps()) {
            event.preventDefault();
          }
        });
      }

      initAjaxSubmit() {
        this.webhookUrl = this.dataset.webhookUrl;

        this.form.addEventListener('submit', (event) => {
          if (this.submitted || event.defaultPrevented) return;
          event.preventDefault();
          this.submitted = true;

          if (this.submitButton) {
            this.submitButton.disabled = true;
            this.submitButton.classList.add('is-loading');
          }

          const requests = [];

          if (this.webhookUrl) {
            const payload = Object.fromEntries(new FormData(this.form));
            payload.submitted_at = new Date().toISOString();

            requests.push(
              fetch(this.webhookUrl, {
                method: 'POST',
                mode: 'no-cors',
                keepalive: true,
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(payload).toString(),
              }).catch(() => {})
            );
          } else {
            console.warn('creators-form: no webhook URL configured, submission was not recorded.');
          }

          Promise.allSettled(requests).then(() => this.showSuccess());
        });
      }

      initDialog() {
        const dialog = this.querySelector('[data-creators-dialog]');
        if (!dialog) return;

        dialog.querySelectorAll('[data-creators-dialog-close]').forEach((button) => {
          button.addEventListener('click', () => dialog.close());
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
      }

      showSuccess() {
        const card = this.querySelector('[data-creators-card]');
        const success = this.querySelector('[data-creators-success]');
        const dialog = this.querySelector('[data-creators-dialog]');

        if (this.submitButton) this.submitButton.classList.remove('is-loading');
        if (card) card.hidden = true;
        if (success) {
          success.hidden = false;
          success.focus({ preventScroll: true });
        }
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (dialog && typeof dialog.showModal === 'function') {
          dialog.showModal();
        }
      }

      showStep(index, moveFocus = true) {
        this.currentStep = Math.max(0, Math.min(index, this.steps.length - 1));
        const isLastStep = this.currentStep === this.steps.length - 1;

        this.steps.forEach((step, i) => {
          step.hidden = i !== this.currentStep;
        });

        this.progressSegments.forEach((segment, i) => {
          segment.classList.toggle('is-active', i <= this.currentStep);
        });
        if (this.progress) {
          this.progress.setAttribute('aria-valuenow', String(this.currentStep + 1));
        }

        if (this.backButton) this.backButton.hidden = this.currentStep === 0;
        if (this.nextButton) this.nextButton.hidden = isLastStep;
        if (this.submitButton) this.submitButton.hidden = !isLastStep;

        if (isLastStep) this.updateSubmitState();

        if (moveFocus) {
          const activeStep = this.steps[this.currentStep];
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          activeStep.focus({ preventScroll: true });
        }
      }
    }
  );
}
